import { createTooltip } from "@gluestack-ui/tooltip";
import { AnimatePresence, AnimatedView } from "@gluestack-style/animation-resolver";
import { styled } from "@gluestack-style/react";
import { View, Text } from "react-native";
const StyledRoot = styled(
	View,
	{
		width: "$full",
		height: "$full",
		_web: {
			pointerEvents: "none",
		},
	},
	{},
);
const StyledContent = styled(
	AnimatedView,
	{
		":initial": {
			opacity: 0,
			scale: 0.5,
		},
		":animate": {
			opacity: 1,
			scale: 1,
		},
		":exit": {
			opacity: 0,
			scale: 0.5,
		},
		":transition": {
			type: "spring",
			damping: 25,
			stiffness: 300,
			// @ts-ignore
			opacity: {
				type: "timing",
				duration: 150,
			},
		},
		py: "$1",
		px: "$3",
		borderRadius: "$sm",
		bg: "$background900",
		_text: {
			fontSize: "$xs",
			color: "$text50",
		},
		_web: {
			pointerEvents: "auto",
		},
		defaultProps: {
			hardShadow: "2",
		},
	},
	{
		descendantStyle: ["_text"],
	},
);
const StyledText = styled(
	Text,
	{
		color: "$textLight700",
		flex: 1,
		fontWeight: "$normal",
		fontFamily: "$body",
		fontStyle: "normal",
		letterSpacing: "$md",
		variants: {
			isTruncated: {
				true: {
					props: {
						// @ts-ignore
						numberOfLines: 1,
						ellipsizeMode: "tail",
					},
				},
			},
			bold: {
				true: {
					fontWeight: "$bold",
				},
			},
			underline: {
				true: {
					textDecorationLine: "underline",
				},
			},
			strikeThrough: {
				true: {
					textDecorationLine: "line-through",
				},
			},
			size: {
				"2xs": {
					fontSize: "$2xs",
				},
				xs: {
					fontSize: "$xs",
				},
				sm: {
					fontSize: "$sm",
				},
				md: {
					fontSize: "$md",
				},
				lg: {
					fontSize: "$lg",
				},
				xl: {
					fontSize: "$xl",
				},
				"2xl": {
					fontSize: "$2xl",
				},
				"3xl": {
					fontSize: "$3xl",
				},
				"4xl": {
					fontSize: "$4xl",
				},
				"5xl": {
					fontSize: "$5xl",
				},
				"6xl": {
					fontSize: "$6xl",
				},
			},
			sub: {
				true: {
					fontSize: "$xs",
				},
			},
			italic: {
				true: {
					fontStyle: "italic",
				},
			},
			highlight: {
				true: {
					bg: "$yellow500",
				},
			},
		},
		defaultProps: {
			size: "md",
		},
	},
	{
		ancestorStyle: ["_text"],
	},
);
const StyledTooltipText = styled(
	StyledText,
	{
		fontWeight: "$normal",
		fontStyle: "normal",
		letterSpacing: "$md",
		variants: {
			isTruncated: {
				true: {
					props: {
						// @ts-ignore
						numberOfLines: 1,
						ellipsizeMode: "tail",
					},
				},
			},
			bold: {
				true: {
					fontWeight: "$bold",
				},
			},
			underline: {
				true: {
					textDecorationLine: "underline",
				},
			},
			strikeThrough: {
				true: {
					textDecorationLine: "line-through",
				},
			},
			size: {
				"2xs": {
					fontSize: "$2xs",
				},
				xs: {
					fontSize: "$xs",
				},
				sm: {
					fontSize: "$sm",
				},
				md: {
					fontSize: "$md",
				},
				lg: {
					fontSize: "$lg",
				},
				xl: {
					fontSize: "$xl",
				},
				"2xl": {
					fontSize: "$2xl",
				},
				"3xl": {
					fontSize: "$3xl",
				},
				"4xl": {
					fontSize: "$4xl",
				},
				"5xl": {
					fontSize: "$5xl",
				},
				"6xl": {
					fontSize: "$6xl",
				},
			},
			sub: {
				true: {
					fontSize: "$xs",
				},
			},
			italic: {
				true: {
					fontStyle: "italic",
				},
			},
			highlight: {
				true: {
					bg: "$yellow500",
				},
			},
		},
		defaultProps: {
			size: "md",
		},
		color: "$red400",
		fontFamily: "$body",
		_web: {
			userSelect: "none",
		},
	},
	{
		ancestorStyle: ["_text"],
	},
);
// Add type declaration for window properties
declare global {
	interface Window {
		tooltipMouseMoveHandler?: (event: MouseEvent) => void;
		tooltipMouseMoveThrottle?: NodeJS.Timeout | null;
	}
}

export const Tooltip = createTooltip({
	Root: StyledRoot,
	Content: StyledContent,
	Text: StyledTooltipText,
	//@ts-ignore
	AnimatePresence: AnimatePresence,
	//@ts-ignore
	_web: {
		onScroll: () => {
			// Force close tooltip on scroll
			const tooltipElements = document.querySelectorAll("[data-tooltip]");
			tooltipElements.forEach((element) => {
				// Use a more efficient approach to close tooltips
				const event = new MouseEvent("mouseout", {
					bubbles: true,
					cancelable: true,
					view: window,
				});
				element.dispatchEvent(event);
			});
		},
		onMouseMove: (e: MouseEvent) => {
			// Use throttling to prevent excessive calls
			if (!window.tooltipMouseMoveHandler) {
				window.tooltipMouseMoveHandler = (event: MouseEvent) => {
					// Check if mouse is over any tooltip trigger
					const tooltipElements = document.querySelectorAll("[data-tooltip]");
					let isOverAnyTooltip = false;

					tooltipElements.forEach((element) => {
						const rect = element.getBoundingClientRect();
						if (event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom) {
							isOverAnyTooltip = true;
						}
					});

					// If mouse is not over any tooltip, close all tooltips
					if (!isOverAnyTooltip) {
						tooltipElements.forEach((element) => {
							const closeEvent = new MouseEvent("mouseout", {
								bubbles: true,
								cancelable: true,
								view: window,
							});
							element.dispatchEvent(closeEvent);
						});
					}
				};
			}
			
			// Throttle the mouse move handler
			if (!window.tooltipMouseMoveThrottle) {
				window.tooltipMouseMoveThrottle = setTimeout(() => {
					window.tooltipMouseMoveHandler?.(e);
					window.tooltipMouseMoveThrottle = null;
				}, 16); // ~60fps
			}
		},
	},
});
export const TooltipContent = Tooltip.Content;
export const TooltipText = Tooltip.Text;
